// services/surveyService.js
import { surveysAPI, surveyLinksAPI, surveyPreviewAPI, transferSurveyAPI } from './api';
import { parseApiError } from '../utils/apiError';

export const surveyService = {
  async getSurveys() {
    try {
      const response = await surveysAPI.getAll();
      
      // Handle different response structures
      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && Array.isArray(response.data.surveys)) {
        return response.data.surveys;
      } else if (Array.isArray(response.data?.items)) {
        return response.data.items;
      } else {
        console.log('📦 Raw API response:', response);
        return response.data || [];
      }
    } catch (error) {
      console.error('❌ Error fetching surveys:', error);
      
      // Enhanced error handling
      if (error.response?.status === 401) {
        throw new Error('Authentication required. Please login again.');
      } else if (error.response?.status === 403) {
        throw new Error('You do not have permission to view surveys.');
      } else if (error.isTimeout) {
        throw new Error('Request timeout. Please check your connection.');
      } else {
        throw new Error(error.response?.data?.message || 'Failed to load surveys');
      }
    }
    
  },

  async getSurvey(id) {
    try {
      const response = await surveysAPI.getById(id);
      
      // Handle different response structures
      if (response.data) {
        return response.data;
      } else {
        console.log('📦 Raw survey response:', response);
        throw new Error('Invalid survey data format');
      }
    } catch (error) {
      console.error('❌ Error fetching survey:', error);
      
      if (error.response?.status === 404) {
        throw new Error('Survey not found');
      } else if (error.response?.status === 403) {
        throw new Error('You do not have permission to view this survey');
      } else {
        throw new Error(error.response?.data?.message || 'Failed to load survey');
      }
    }
  },
  // In your surveyService.js, replace these methods:

getPublicSurvey: async (surveyId, linkKey) => {
  try {
    console.log('📡 Fetching public survey:', { surveyId, linkKey });
    
    // Use surveysAPI instead of undefined 'api'
    const response = await surveysAPI.getPublicSurvey(surveyId, {
      params: { key: linkKey }
    });
    
    console.log('✅ Public survey API response:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching public survey:', error);
    throw new Error(error.response?.data?.message || 'Failed to load survey');
  }
},

submitPublicSurveyResponses: async (surveyId, linkKey, responses) => {
  try {
    console.log('📤 Submitting public survey responses:', { surveyId, linkKey, responses });
    
    const response = await surveysAPI.submitPublicSurveyResponses(surveyId, {
      key: linkKey,
      responses
    });
    
    console.log('✅ Survey responses submitted:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error submitting survey responses:', error);
    throw new Error(error.response?.data?.message || 'Failed to submit survey');
  }
},
  async createSurvey(survey) {
    try {
      console.log('📤 Creating survey:', survey);
      const response = await surveysAPI.create(survey);
      console.log('✅ Survey created:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating survey:', error);
      // Surface the real validation reason(s) instead of ASP.NET's generic
      // "One or more validation errors occurred." title (see utils/apiError).
      throw parseApiError(error, 'Failed to create survey');
    }
  },

  async updateSurvey(id, survey) {
    try {
      console.log(`📤 Updating survey ${id}:`, survey);
      const response = await surveysAPI.update(id, survey);
      console.log('✅ Survey updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating survey:', error);
      throw parseApiError(error, 'Failed to update survey');
    }
  },

  async deleteSurvey(id) {
    try {
      await surveysAPI.delete(id);
    } catch (error) {
      console.error('❌ Error deleting survey:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete survey');
    }
  },

  async getSurveyCategories(surveyId) {
    try {
      const response = await surveysAPI.getCategories(surveyId);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching survey categories:', error);
      throw new Error(error.response?.data?.message || 'Failed to load survey categories');
    }
  },

  async getSurveyResponses(surveyId) {
    try {
      const response = await surveysAPI.getResponses(surveyId);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching survey responses:', error);
      throw new Error(error.response?.data?.message || 'Failed to load survey responses');
    }
  },

  async reorderCategories(surveyId, categoryOrders) {
    try {
      const response = await surveysAPI.reorderCategories(surveyId, categoryOrders);
      return response.data;
    } catch (error) {
      console.error('❌ Error reordering categories:', error);
      throw new Error(error.response?.data?.message || 'Failed to reorder categories');
    }
  },

  // ===== SURVEY LINK & DISTRIBUTION FEATURES =====
  async generateSurveyLink(surveyId) {
    try {
      console.log('🔗 Generating survey link for survey:', surveyId);
      const response = await surveyLinksAPI.generate(surveyId);
      console.log('✅ Survey link generated:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error generating survey link:', error);
      const errorMessage = error.response?.data?.message || 
                          'Failed to generate survey link';
      throw new Error(errorMessage);
    }
  },

  async getSurveyLinkDetails(surveyId) {
    try {
      const response = await surveyLinksAPI.getSurveyLinks(surveyId);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching survey link details:', error);
      throw new Error(error.response?.data?.message || 'Failed to load survey link details');
    }
  },

  async generateQRCode(surveyId, linkType = 'public') {
    try {
      console.log('📱 Generating QR code for survey:', surveyId, 'type:', linkType);
      const response = await surveyLinksAPI.generateQRCode(surveyId, linkType);
      console.log('✅ QR code generated');
      return response.data;
    } catch (error) {
      console.error('❌ Error generating QR code:', error);
      throw new Error(error.response?.data?.message || 'Failed to generate QR code');
    }
  },

  async getSurveyLinks(surveyId) {
    try {
      const response = await surveyLinksAPI.getSurveyLinks(surveyId);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching survey links:', error);
      throw new Error(error.response?.data?.message || 'Failed to load survey links');
    }
  },

  async getQRCodeAsBase64(surveyId, linkType = 'public') {
    try {
      console.log('🔄 Fetching QR code as base64 for:', surveyId);
      
      // Try to get QR code from backend first
      try {
        const response = await surveyLinksAPI.generateQRCode(surveyId, linkType);
        
        // If backend returns QR data, use it
        if (response.data && response.data.qrData) {
          // If it's already a data URL, return it
          if (response.data.qrData.startsWith('data:')) {
            return response.data.qrData;
          }
          
          // Otherwise, assume it's a URL and create a QR code from it
          const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(response.data.qrData)}`;
          return qrUrl;
        }
      } catch (backendError) {
        console.log('⚠️ Backend QR generation failed, using fallback:', backendError.message);
      }
      
      // Fallback: Use external QR service
      const surveyLink = `${window.location.origin}/survey/${surveyId}`;
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(surveyLink)}`;
      
      console.log('✅ Using external QR code service:', qrUrl);
      return qrUrl;
      
    } catch (error) {
      console.error('❌ Error generating QR code as base64:', error);
      
      // Return a placeholder image as last resort
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YzZjNmMyIvPjx0ZXh0IHg9IjEwMCIgeT0iMTAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiPlFSIENvZGUgRXJyb3I8L3RleHQ+PC9zdmc+';
    }
  },

  async downloadQRCode(surveyId, filename = null) {
    try {
      console.log('🔄 Downloading QR code for:', surveyId);
      
      // Try backend download first
      try {
        const response = await surveyLinksAPI.downloadQRCode(surveyId);
        
        if (response.data instanceof Blob) {
          const url = window.URL.createObjectURL(response.data);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename || `survey-${surveyId}-qrcode.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          
          console.log('✅ QR code downloaded successfully');
          return { success: true, message: 'QR code downloaded successfully' };
        }
      } catch (backendError) {
        console.log('⚠️ Backend QR download failed, using fallback:', backendError.message);
      }
      
      // Fallback: Create QR code using external service
      const surveyLink = `${window.location.origin}/survey/${surveyId}`;
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(surveyLink)}`;
      
      const response = await fetch(qrUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `survey-${surveyId}-qrcode.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ Fallback QR code downloaded');
      return { success: true, message: 'QR code downloaded via fallback service' };
      
    } catch (error) {
      console.error('❌ Error downloading QR code:', error);
      throw new Error('Failed to download QR code: ' + error.message);
    }
  },

  async copyToClipboard(text) {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      console.log('✅ Text copied to clipboard');
      return { success: true, message: 'Copied to clipboard' };
    } catch (error) {
      console.error('❌ Error copying to clipboard:', error);
      return { success: false, message: 'Failed to copy to clipboard' };
    }
  },

  openLinkInNewTab(url) {
    try {
      if (!url) {
        throw new Error('URL is required');
      }
      window.open(url, '_blank', 'noopener,noreferrer');
      console.log('✅ Link opened in new tab:', url);
      return { success: true, message: 'Link opened in new tab' };
    } catch (error) {
      console.error('❌ Error opening link:', error);
      return { success: false, message: 'Failed to open link' };
    }
  },

  async regeneratePrivateLink(surveyId) {
    try {
      console.log('🔄 Regenerating private link for:', surveyId);
      const response = await surveyLinksAPI.regeneratePrivateLink(surveyId);
      console.log('✅ Private link regenerated:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error regenerating private link:', error);
      throw new Error('Failed to regenerate private link: ' + error.message);
    }
  },

  async regeneratePublicLink(surveyId) {
    try {
      console.log('🔄 Regenerating public link for:', surveyId);
      const response = await surveyLinksAPI.regeneratePublicLink(surveyId);
      console.log('✅ Public link regenerated:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error regenerating public link:', error);
      throw new Error('Failed to regenerate public link: ' + error.message);
    }
  },

  async testPreviewLink() {
    try {
      console.log('🔄 Testing preview link');
      const response = await surveyLinksAPI.testPreviewLink();
      console.log('✅ Preview link test result:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error testing preview link:', error);
      throw new Error('Failed to test preview link: ' + error.message);
    }
  },

  async validateLink(url, surveyId = null) {
    try {
      console.log('🔄 Validating survey link:', url);
      const response = await surveyLinksAPI.validateLink({ url, surveyId });
      console.log('✅ Link validation result:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error validating link:', error);
      throw error;
    }
  },

  // ===== SURVEY PREVIEW & VALIDATION FEATURES =====
  async getSurveyPreview(surveyId) {
  try {
    console.log('🔄 Fetching survey preview for ID:', surveyId);
    
    const response = await surveyPreviewAPI.getPreview(surveyId);
    console.log('📦 Raw API response:', response);
    console.log('📊 Response data:', response.data);
    
    if (!response.data) {
      throw new Error('No data received from server');
    }
    
    return response.data;
  } catch (error) {
    console.error('❌ Error in getSurveyPreview:', {
      surveyId,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message
    });
    
    throw new Error(error.response?.data?.message || 'Failed to load survey preview');
  }
},

  async getSurveyBasicInfo(surveyId) {
    try {
      const response = await surveyPreviewAPI.getBasicInfo(surveyId);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching survey basic info:', error);
      throw new Error(error.response?.data?.message || 'Failed to load survey basic info');
    }
  },

  async validateSurvey(surveyId) {
    try {
      const preview = await this.getSurveyPreview(surveyId);
      
      // Handle different response structures
      const surveyData = preview.survey || preview.Survey || preview;
      const categories = surveyData.categories || surveyData.Categories || [];
      
      // Check if survey has categories
      if (!categories || categories.length === 0) {
        throw new Error('Survey has no categories');
      }

      // Check if categories have questions
      const categoriesWithQuestions = categories.filter(
        category => {
          const questions = category.questions || category.Questions || [];
          return questions && questions.length > 0;
        }
      );

      if (categoriesWithQuestions.length === 0) {
        throw new Error('Survey has no questions');
      }

      const totalQuestions = categories.reduce(
        (total, category) => {
          const questions = category.questions || category.Questions || [];
          return total + (questions?.length || 0);
        }, 0
      );

      return {
        isValid: true,
        categoriesCount: categories.length,
        questionsCount: totalQuestions,
        categoriesWithQuestions: categoriesWithQuestions.length,
        message: 'Survey is ready for distribution'
      };
    } catch (error) {
      console.error('❌ Survey validation failed:', error);
      return {
        isValid: false,
        error: error.message,
        categoriesCount: 0,
        questionsCount: 0,
        categoriesWithQuestions: 0,
        message: error.message
      };
    }
  },

  async getSurveyStatistics(surveyId) {
    try {
      const preview = await this.getSurveyPreview(surveyId);
      
      // Handle different response structures
      const surveyData = preview.survey || preview.Survey || preview;
      const categories = surveyData.categories || surveyData.Categories || [];

      const statistics = {
        totalCategories: categories.length,
        totalQuestions: 0,
        requiredQuestions: 0,
        questionTypes: {},
        estimatedTime: 0,
        completionRate: 0
      };

      // Calculate statistics
      categories.forEach(category => {
        const questions = category.questions || category.Questions || [];
        statistics.totalQuestions += questions.length;

        questions.forEach(question => {
          // Count required questions
          if (question.isRequired || question.IsRequired) {
            statistics.requiredQuestions++;
          }

          // Count question types
          const type = question.type || question.Type;
          const typeName = this.getQuestionTypeName(type);
          statistics.questionTypes[typeName] = 
            (statistics.questionTypes[typeName] || 0) + 1;

          // Estimate time based on question type (in seconds)
          const timeEstimates = {
            0: 30, // Text: 30 seconds
            1: 45, // TextArea: 45 seconds
            2: 15, // Radio: 15 seconds
            3: 20, // Checkbox: 20 seconds
            4: 10  // Rating: 10 seconds
          };
          statistics.estimatedTime += timeEstimates[type] || 30;
        });
      });

      // Convert to minutes
      statistics.estimatedTime = Math.ceil(statistics.estimatedTime / 60);

      return statistics;
    } catch (error) {
      console.error('❌ Error calculating survey statistics:', error);
      // Return default statistics instead of throwing
      return {
        totalCategories: 0,
        totalQuestions: 0,
        requiredQuestions: 0,
        questionTypes: {},
        estimatedTime: 0,
        completionRate: 0
      };
    }
  },

  async isSurveyReady(surveyId) {
    try {
      const [validation, statistics, linkDetails] = await Promise.all([
        this.validateSurvey(surveyId),
        this.getSurveyStatistics(surveyId),
        this.getSurveyLinkDetails(surveyId).catch(error => {
          console.warn('⚠️ Could not load link details:', error.message);
          return null; // Return null instead of failing the entire promise
        })
      ]);
      
      const recommendations = this.generateRecommendations(validation, statistics);
      
      return {
        ready: validation.isValid,
        validation,
        statistics,
        linkDetails,
        recommendations,
        canDistribute: validation.isValid && statistics.totalQuestions > 0
      };
    } catch (error) {
      console.error('❌ Error checking survey readiness:', error);
      return {
        ready: false,
        validation: { isValid: false, error: error.message },
        statistics: null,
        linkDetails: null,
        recommendations: ['Survey validation failed'],
        canDistribute: false
      };
    }
  },

  // ===== ENHANCED METHODS =====
  async getSurveyWithPreview(id) {
    try {
      const [survey, preview, statistics] = await Promise.all([
        this.getSurvey(id),
        this.getSurveyPreview(id),
        this.getSurveyStatistics(id)
      ]);

      return {
        ...survey,
        preview: preview.Survey,
        statistics,
        hasPreview: true,
        isValid: true
      };
    } catch (error) {
      console.error('❌ Error fetching survey with preview:', error);
      throw error;
    }
  },

  // ===== UTILITY METHODS =====
  getQuestionTypeName(typeCode) {
    const types = {
      0: 'Text',
      1: 'TextArea', 
      2: 'Radio',
      3: 'Checkbox',
      4: 'Rating'
    };
    return types[typeCode] || 'Unknown';
  },

  generateRecommendations(validation, statistics) {
    const recommendations = [];

    if (!validation.isValid) {
      if (validation.categoriesCount === 0) {
        recommendations.push('Add at least one category to organize your questions');
      }
      if (validation.questionsCount === 0) {
        recommendations.push('Add questions to your survey categories');
      }
    }

    if (statistics.totalQuestions === 0) {
      recommendations.push('Add questions to make your survey meaningful');
    }

    if (statistics.estimatedTime > 10) {
      recommendations.push(`Survey is quite long (estimated ${statistics.estimatedTime} minutes). Consider shortening it for better completion rates.`);
    }

    if (statistics.requiredQuestions === 0) {
      recommendations.push('Consider adding some required questions for better data quality');
    }

    if (statistics.totalQuestions < 3) {
      recommendations.push('Add more questions to gather comprehensive feedback');
    }

    // Add positive feedback if survey is well-structured
    if (validation.isValid && statistics.totalQuestions >= 5 && statistics.estimatedTime <= 5) {
      recommendations.push('✅ Survey is well-structured and should provide good insights!');
    }

    return recommendations;
  },

  // ===== BATCH OPERATIONS =====
  async duplicateSurvey(surveyId, newName = null) {
    try {
      const originalSurvey = await this.getSurveyWithPreview(surveyId);
      const newSurvey = {
        ...originalSurvey,
        id: undefined,
        name: newName || `${originalSurvey.name} (Copy)`,
        createdDate: new Date().toISOString(),
        updatedDate: new Date().toISOString()
      };

      // Remove preview data before creating
      delete newSurvey.preview;
      delete newSurvey.statistics;
      delete newSurvey.hasPreview;

      return await this.createSurvey(newSurvey);
    } catch (error) {
      console.error('❌ Error duplicating survey:', error);
      throw new Error('Failed to duplicate survey');
    }
  },

  async exportSurveyData(surveyId) {
    try {
      const [surveyData, responses, links] = await Promise.all([
        this.getSurveyWithPreview(surveyId),
        this.getSurveyResponses(surveyId),
        this.getSurveyLinks(surveyId)
      ]);

      const exportData = {
        survey: surveyData,
        responses: responses || [],
        links: links || [],
        exportDate: new Date().toISOString(),
        exportVersion: '1.0'
      };

      // Create downloadable JSON file
      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `survey-${surveyId}-export-${new Date().getTime()}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      return exportData;
    } catch (error) {
      console.error('❌ Error exporting survey data:', error);
      throw new Error('Failed to export survey data');
    }
  },

  // ===== SURVEY STRUCTURE METHODS =====
  async getSurveyStructure(surveyId) {
    try {
      console.log('🔄 Fetching survey structure for:', surveyId);
      const response = await surveysAPI.getById(surveyId);
      console.log('✅ Survey structure loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching survey structure:', error);
      throw new Error('Failed to load survey structure: ' + error.message);
    }
  }
};

export const transferSurveyService = {
  getCompanies: async () => {
    try {
      const response = await transferSurveyAPI.getCompanies();
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching companies:', error);
      throw new Error(error.response?.data?.message || 'Failed to load companies');
    }
  },

  getSurveysByCompany: async (companyId) => {
    try {
      const response = await transferSurveyAPI.getSurveysByCompany(companyId);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching surveys:', error);
      throw new Error(error.response?.data?.message || 'Failed to load surveys');
    }
  },

  getSurveyDetails: async (surveyId) => {
    try {
      const response = await transferSurveyAPI.getSurveyDetails(surveyId);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching survey details:', error);
      throw new Error(error.response?.data?.message || 'Failed to load survey details');
    }
  },

  transferSurvey: async (transferData) => {
    try {
      const response = await transferSurveyAPI.transferSurvey(transferData);
      return response.data;
    } catch (error) {
      console.error('❌ Error transferring survey:', error);
      throw new Error(error.response?.data?.message || 'Failed to transfer survey');
    }
  }
};

// Make sure you're exporting it
export default {
  surveyService,
  transferSurveyService
};